/**
 * windDown.js — RW-899 stage 1 ("winding_down") route allowlist.
 *
 * Two-stage offboarding model:
 *   Stage 1 — starting an offboarding (offboardingApi `start`) sets
 *     users/{uid}.agentStatus.status = 'winding_down'. The agent keeps
 *     sign-in, but the app restricts them to the Deals/transactions module
 *     plus account basics (Settings, notifications, sign-out) so they can
 *     close out their business. Canceling the offboarding restores 'active'.
 *   Stage 2 — markDeparted / checklist completion sets 'terminated' +
 *     active:false, and the onUserLifecycle trigger disables Firebase Auth,
 *     revokes sessions, and strips privilege claims.
 *
 * ONE source of truth for what stays reachable in stage 1 — consumed by both
 * the App.js route gate and the Sidebar nav filter so they can never drift.
 *
 * RW-899: winding_down is a WORKFLOW restriction (client-gated), NOT a
 * security boundary. Server callables keep their own authorization, and
 * stage 2 is where access is genuinely revoked.
 */
import { legacyDeepLinkTarget } from '../components/LegacyDeepLinkRedirect';

export const WIND_DOWN_ALLOWED_PREFIXES = [
  '/deals', // One Pipeline (RW-1524): the deal book, record, tasks — the wind-down workspace
  '/settings', // account basics
  '/notifications', // the notification bell's full page
  // RW-3878 (Clint, 2026-10-21): NOT the Market boards (Coming Soon, Buyer
  // Needs) — a winding-down agent does not see them. RW-3647 had listed
  // /connect/coming-soon and /connect/buyer-needs here to keep reach that was
  // only ever an accident of the boards living under /deals before the move.
];

const onAllowlist = (path) => WIND_DOWN_ALLOWED_PREFIXES.some(
  (p) => path === p || path.startsWith(p + '/'),
);

/**
 * Is this path reachable for a winding-down agent?
 * '/' is the wind-down screen itself (App.js renders it there while the
 * smart login redirect is suppressed).
 */
export function isWindDownAllowedPath(pathname) {
  if (pathname === '/') return true;
  // RW-3878: a legacy path is only a redirect, so it is judged by where it
  // lands. The pre-move /deals/coming-soon and /deals/buyer-needs sit under
  // the allowed '/deals' prefix but forward to the Market boards — the rail
  // and the route gate must not treat them as Deals pages.
  const target = legacyDeepLinkTarget(pathname);
  if (target) return onAllowlist(target.split(/[?#]/)[0]);
  return onAllowlist(pathname);
}
